import React, {useEffect, useMemo, useRef, useState} from 'react';
import {useDispatch, useSelector} from "react-redux";
import {Spin} from "antd";
import {setError, setHideFooter, updateCurrStep} from "../../../../../../redux/stepperSlice";
import {t} from "../../../../../../services/i18n";
import {callManageLog, getMessageToUserFromError, updatePaymentProcessInDB} from "../../../../../../services/Helpers";
import {useToast} from "react-native-toast-notifications";
import {paymentLogs} from "../../../../../../services/ConstHelper";

const Payment = (props) => {
    const { values, setFieldValue, handleSubmit, cartOverride, membership_user_id, locationOverride } = props;
    const user = useSelector((state) => state.stepper.props?.user);
    const error = useSelector((state) => state.stepper.error);
    const box = useSelector(state => state.box.box)
    const lang = useSelector(state => state.userSettings.lang);
    const token = useSelector(state => state.auth?.data?.token);
    const flowConfig = useSelector(state => state.stepper)
    const [submitted, setSubmitted] = useState(false);
    const [iframeSrc, setIframeSrc] = useState(null);
    const [loading, setLoading] = useState(true);
    const [gotInitMessage, setGotInitMessage] = useState(false);
    const [communicationError, setCommunicationError] = useState(false);

    const dispatch = useDispatch()
    const toast = useToast();
    const gotInitMessageRef = useRef(gotInitMessage);

    const boxLang = useMemo(() => lang?.language === 'Hebrew' ? 'he' : 'en', [lang])
    const cart = useMemo(() => cartOverride ?? values.cart ?? [], [cartOverride, values.cart])
    const locationId = useMemo(() => locationOverride ?? (cart.length ? cart[0].location_box_fk : null), [cart, locationOverride])
    const hideBack = useMemo(() => flowConfig.steps.length === 1, [flowConfig.steps])

    useEffect(() => {
        dispatch(setHideFooter(true))
        waitForInitMessage()
        listenToMessages()
        return () => {
            callManageLog(paymentLogs.stopListeningResponseLog, {})
            window.removeEventListener("message", listenToMessages);
            setSubmitted(false)
            dispatch(setHideFooter(false))
        };
    }, []);

    useEffect(() => {
        if(locationId && boxLang && (cart.length || membership_user_id)) {
            const iframe = getIframeSrc()
            setIframeSrc(iframe)
        }
    }, [locationId, boxLang, user, cart, membership_user_id]);

    useEffect(() => {
        setFieldValue('user', user ?? null)
    }, [user]);

    useEffect(() => {
        if(values.payment_info && !submitted) {
            callManageLog(paymentLogs.submitLog, values)
            handleSubmit()
            setSubmitted(true)
        }
    }, [values.payment_info, submitted]);

    useEffect(() => {
        if(error && error !== '') {
            setFieldValue('payment_info', null)
            setIframeSrc(null)
            setSubmitted(false)
            callManageLog(paymentLogs.errorResponseLog, {error, values})
            console.log('error',error)
            const errorMessage = getMessageToUserFromError(error)
            toast.show(errorMessage !== '' ? errorMessage : t('payment-error'), { type: 'danger', duration: 5000 });
            setTimeout(() => setIframeSrc(getIframeSrc()), 10)
            dispatch(setError(null))
        }
    }, [error]);

    useEffect(() => {
        gotInitMessageRef.current = gotInitMessage;
    }, [gotInitMessage]);

    useEffect(() => {
        callManageLog(paymentLogs.iframeLog, {iframeSrc, values})
    }, [iframeSrc]);

    const listenToMessages = () => {
        window.addEventListener('message', (event) => {
            if(event.data.type === "PAYMENT_RESPONSE_SUCCESS") {
                console.log('listening.. ', event.data.data)
                setTimeout(() => {
                    callManageLog(paymentLogs.successResponseLog, event.data.data)
                    setFieldValue('payment_info', event.data.data)
                }, 10)
            }
            if(event.data.type === "PAYMENT_MANAGE_BACK") {
                const stepIndex = flowConfig.steps.indexOf(flowConfig?.currStep);
                dispatch(updateCurrStep(flowConfig.steps[stepIndex - 1]))
            }
            if(event.data.type === "PAYMENT_RESPONSE_ERROR") {
                console.log('error!!!! ', event.data)
                callManageLog(paymentLogs.errorResponseLog, event.data)
                dispatch(setError({messageToUser: t('payment-generic-error'), message: 'payment-error'}))
            }
            if(event.data.type === "PAYMENT_LISTENER_CHECK") {
                setGotInitMessage(true)
                const uuid = event.data.data?.uuidPayment
                if(uuid) {
                    updatePaymentProcessInDB({uuid: uuid, params: {values}})
                    setFieldValue('uuidPayment', uuid)
                }
                callManageLog(paymentLogs.gotInitMessageLog, {})
            }
        });
    }

    const waitForInitMessage = () => {
        setTimeout(() => {
            //if gotInitMessage is false after 5 seconds, stop payment process completely
            if(!gotInitMessageRef.current) {
                callManageLog(paymentLogs.noInitMessageLog, {})
                setFieldValue('payment_info', null)
                setIframeSrc(null)
                setSubmitted(false)
                toast.show(t('payment-communication-error'), { type: 'danger', duration: 5000 });
                setCommunicationError(true)
            }
        }, 8000)
    }

    // name and phone are only for meshulam
    const getUserName = () => {
        const firstName = user?.first_name ?? values?.first_name ?? values?.child?.first_name
        const lastName = user?.last_name ?? values?.last_name ?? values?.child?.last_name
        if(firstName) {
            if(firstName.length >= 2) {
                return `&name=${firstName} ${lastName || ''}`
            }
        }

        return ''
    }

    const getUserPhone = () => {
        const phone = user?.phone ?? values?.phone
        if(phone && (phone[0] === '0' || phone.startsWith('972')) && phone.length >= 10) {
            return `&phone=${phone}`
        }
        return ''
    }

    const getStartDates = () => {
        if(cart.some(item => item.start)) {
            const cartWithDates = cart.reduce((acc, item) => (item.start ? {[item.id]: item.start} : acc), {})
            return Object.keys(cartWithDates).length ? `&startDates=${JSON.stringify(cartWithDates)}` : ''
        }
        return ''
    }

    const getIframeSrc = () => {
        return `${process.env.REACT_APP_PAYMENTS_URL}?box_id=${box.id}&lang=${boxLang}&locations_box_id=${locationId}&userId=${user?.user_fk ?? null}&cart=${JSON.stringify(cart.map(item => item.id))}&staffToken=${token}&membershipUserId=${membership_user_id ?? null}&hideBack=${hideBack}${getUserName()}${getUserPhone()}${getStartDates()}`
    }

    if(communicationError) return null

    return (
        <div style={{display: 'flex', flexDirection: 'column', flex: 1}}>
            <Spin spinning={loading} style={{alignSelf: 'center', justifyContent:'center', flex: 1}}/>
            {iframeSrc &&
                <iframe frameBorder={0}
                        style={{width: '100%', flex: 1}}
                        onLoad={() => setLoading(false)}
                        src={iframeSrc}/>}
        </div>
    );
};

export default Payment;